import { useMutation } from '@apollo/client';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { DeleteAssessmentResultsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteAssessmentResultsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteDocumentAssessmentResultsTRPC } from './useDeleteDocumentAssessmentResultsTRPC';
import { useDeleteObligationAssessmentResultsTRPC } from './useDeleteObligationAssessmentResultsTRPC';
import { useDeleteRiskAssessmentResultsTRPC } from './useDeleteRiskAssessmentResultsTRPC';
import { useDeleteTestResultsForAssessmentsTRPC } from './useDeleteTestResultsForAssessmentsTRPC';

type AssessmentResultEntityType =
  | 'risk_assessment_result'
  | 'document_assessment_result'
  | 'obligation_assessment_result'
  | 'test_result';

/**
 * Maps GraphQL __typename values to the base entity type for per-type
 * tRPC delete endpoints. Only the 4 base assessment result types are
 * supported — internal audit and second-line result types are separate
 * tables that require their own delete endpoints (not yet migrated).
 */
const mapTypenameToEntityType = (
  typename: string
): AssessmentResultEntityType | null => {
  switch (typename) {
    case 'risk_assessment_result':
      return 'risk_assessment_result';
    case 'document_assessment_result':
      return 'document_assessment_result';
    case 'obligation_assessment_result':
      return 'obligation_assessment_result';
    case 'test_result':
      return 'test_result';
    default:
      // Internal audit and second-line result types are separate tables
      // not covered by the base assessment result delete endpoints.
      return null;
  }
};

/**
 * Groups items by their mapped entity type and returns a map of
 * entity type to array of IDs, plus any unmapped IDs that need
 * to fall back to GraphQL.
 */
const groupIdsByEntityType = (
  items: { Id: string; typename: string }[]
): {
  grouped: Record<AssessmentResultEntityType, string[]>;
  unmappedIds: string[];
} => {
  const grouped: Record<AssessmentResultEntityType, string[]> = {
    risk_assessment_result: [],
    document_assessment_result: [],
    obligation_assessment_result: [],
    test_result: [],
  };
  const unmappedIds: string[] = [];

  for (const item of items) {
    const entityType = mapTypenameToEntityType(item.typename);
    if (entityType) {
      grouped[entityType].push(item.Id);
    } else {
      unmappedIds.push(item.Id);
    }
  }

  return { grouped, unmappedIds };
};

/**
 * Maps tRPC response back to GraphQL mutation shape for compatibility
 */
const mapTrpcResponseToGraphQL = (
  numIds: number
): DeleteAssessmentResultsMutation => ({
  delete_document_assessment_result: {
    __typename: 'document_assessment_result_mutation_response',
    affected_rows: numIds,
  },
  delete_obligation_assessment_result: {
    __typename: 'obligation_assessment_result_mutation_response',
    affected_rows: numIds,
  },
  delete_risk_assessment_result: {
    __typename: 'risk_assessment_result_mutation_response',
    affected_rows: numIds,
  },
  delete_test_result: {
    __typename: 'test_result_mutation_response',
    affected_rows: numIds,
  },
  delete_relation_file: {
    __typename: 'relation_file_mutation_response',
    affected_rows: numIds,
  },
});

export const useDeleteAssessmentResults = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const { addNotification } = useNotifications();

  // GraphQL mutation
  const [deleteAssessmentResultsGraphQL, graphqlState] = useMutation(
    DeleteAssessmentResultsDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk_assessment_result');
        evictField(cache, 'document_assessment_result');
        evictField(cache, 'obligation_assessment_result');
        evictField(cache, 'test_result');
        evictField(cache, 'assessment');
        evictField(cache, 'risk_assessment_result_aggregate');
        evictField(cache, 'risk_score');
      },
      onError: (error) => {
        if (!trpcEnabled) {
          addNotification({
            type: 'error',
            content: error.message,
          });
        }
      },
    }
  );

  // Per-type tRPC mutations
  const riskMutation = useDeleteRiskAssessmentResultsTRPC();
  const documentMutation = useDeleteDocumentAssessmentResultsTRPC();
  const obligationMutation = useDeleteObligationAssessmentResultsTRPC();
  const testResultMutation = useDeleteTestResultsForAssessmentsTRPC();

  const deleteAssessmentResults = async (variables: {
    items: { Id: string; typename: string }[];
  }): Promise<DeleteAssessmentResultsMutation> => {
    if (trpcEnabled) {
      const { grouped, unmappedIds } = groupIdsByEntityType(variables.items);
      const promises: Promise<void>[] = [];

      if (grouped.risk_assessment_result.length > 0) {
        promises.push(
          riskMutation.deleteRiskAssessmentResults(
            grouped.risk_assessment_result
          )
        );
      }
      if (grouped.document_assessment_result.length > 0) {
        promises.push(
          documentMutation.deleteDocumentAssessmentResults(
            grouped.document_assessment_result
          )
        );
      }
      if (grouped.obligation_assessment_result.length > 0) {
        promises.push(
          obligationMutation.deleteObligationAssessmentResults(
            grouped.obligation_assessment_result
          )
        );
      }
      if (grouped.test_result.length > 0) {
        promises.push(
          testResultMutation.deleteTestResults(grouped.test_result)
        );
      }

      // Internal audit and second-line result types are separate tables
      // not yet migrated to tRPC — log a warning and skip for now
      if (unmappedIds.length > 0) {
        const unmappedTypes = variables.items
          .filter((i) => !mapTypenameToEntityType(i.typename))
          .map((i) => i.typename);
        console.warn(
          `[useDeleteAssessmentResults] Skipping ${unmappedIds.length} item(s) with unmapped types: ${[...new Set(unmappedTypes)].join(', ')}.`
        );
      }

      await Promise.all(promises);

      return mapTrpcResponseToGraphQL(variables.items.length);
    }

    // GraphQL fallback: extract all IDs and call the single mutation
    const result = await deleteAssessmentResultsGraphQL({
      variables: {
        Ids: variables.items.map((i) => i.Id),
      },
    });

    if (!result.data) {
      throw new Error('Failed to delete assessment results');
    }

    return result.data;
  };

  const trpcLoading =
    riskMutation.isLoading ||
    documentMutation.isLoading ||
    obligationMutation.isLoading ||
    testResultMutation.isLoading;

  return {
    deleteAssessmentResults,
    deleteResult: {
      loading: trpcEnabled ? trpcLoading : graphqlState.loading,
    },
  };
};
